<script setup lang="ts">
import { computed } from 'vue';
import { sortBy } from 'lodash';

import type { User } from '@models';
import { useStore } from '@store';
import { RoleId } from '@values/roles';
/**
 * `TableCellUserRoles` component properties.
 */
export interface Props {
  /** ... */
  item: User;
}

const store = useStore();

const props = defineProps<Props>();

const roleId = computed(() => {
  return store.state.me.selectedRole?.roleId;
});

const isLasAdmin = computed(() => {
  return roleId.value === RoleId.LasAdmin;
});

const organizationId = computed(() => {
  return store.state.me.selectedRole?.organization?.id;
});

/** ... */
const roles = computed(() => {
  let items = props.item.roles ?? [];

  if (!isLasAdmin.value && organizationId.value) {
    items = items.filter(
      ({ organization }) => organization?.id === organizationId.value,
    );
  }

  return sortBy(items, ['id']).reverse();
});
</script>

<script lang="ts">
declare module '@vue/runtime-core' {
  export interface GlobalComponents {
    TableCellUserRoles: ComponentWithProps<Props>;
  }
}

export default { name: 'TableCellUserRoles' };
</script>

<template>
  <div class="user-roles-table-cell">
    <template v-if="roles.length">
      <div
        v-for="role in roles"
        :key="role.id"
        :class="[
          'role-item',
          role.name ? role.name.toLowerCase().replace(' ', '-') : '',
        ]"
      >
        <span class="role-name">{{ role.name }}</span>

        <span
          v-if="role.organization?.name && isLasAdmin"
          class="role-institution"
        >
          <span class="sub-value">@{{ role.organization.name }}</span>
        </span>

        <span v-if="role.course?.name" class="role-course">
          <span class="sub-label">Course:</span>
          <span class="sub-value">{{ role.course.name }}</span>
        </span>
      </div>
    </template>

    <span v-else> ... </span>
  </div>
</template>

<style scoped lang="scss">
.user-roles-table-cell {
  white-space: nowrap;
  font-size: 0.8em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.role-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  gap: 2px;
  width: fit-content;

  &.student {
    color: $role-pill-student-color;
    background-color: $role-pill-student-background;
  }

  &.instructor {
    color: $role-pill-instructor-color;
    background-color: $role-pill-instructor-background;
  }

  &.institution-admin {
    color: $role-pill-institution-admin-color;
    background-color: $role-pill-institution-admin-background;
  }

  &.independent-operator {
    color: $role-pill-independent-operator-color;
    background-color: $role-pill-independent-operator-background;
  }

  &.subscriber {
    color: $role-pill-subscriber-color;
    background-color: $role-pill-subscriber-background;
  }

  &.administrator {
    color: $role-pill-administrator-color;
    background-color: $role-pill-administrator-background;
  }

  &.reseller {
    color: $role-pill-reseller-color;
    background-color: $role-pill-reseller-background;
  }

  &.reviewer {
    color: $role-pill-reviewer-color;
    background-color: $role-pill-reviewer-background;
  }
}

.sub-label {
  font-weight: bold;
}

.sub-value {
  opacity: 0.75;
}
</style>
